// ============================================================
// ANSICHTSSACHE-AGENT – Bausteine des Filterkopfs
// ============================================================
// JEDE Listenseite im Agent traegt denselben Kopf. Nicht „einen
// aehnlichen" — DENSELBEN: die Knoepfe hier sind die einzige
// Fassung davon, die es gibt.
//
// Warum das ein eigener Ordner ist und keine Kopiervorlage: die
// Rechnungsliste und die Projektliste hatten beide einen
// Statusfilter, beide eine Suche, beide Personenkreise — und
// jeder davon sass woanders, hiess anders und sah anders aus.
// Am selben Tag stand auf der einen Seite ein Knopf „Kunde", der
// FILTERT, und auf der anderen ein Knopf „Kunde", der GRUPPIERT.
// Zwei Fassungen desselben Gedankens laufen immer auseinander;
// eine gemeinsame kann es nicht.
//
// Der Aufbau, den diese Bausteine erzwingen:
//
//   Zeile 1   Suche · Waehler (Sachfilter) ······ Zaehler rechts
//   Zeile 2   Personenkreise ·········· „Filter zurücksetzen (n)"
//
// WAS RECHTS STEHT, STEHT DORT NUR, SOLANGE DIE ZEILE REICHT.
// Bricht sie um, faengt der umgebrochene Teil links an derselben
// Kante an wie die Tabelle darunter. Dafuer gibt es `Luecke`;
// `ml-auto` kann das nicht (siehe dort).
//
// GEWAEHLT HEISST: DIE FLAECHE LIEGT OBEN. Die Knoepfe stehen
// auf einer grauen Schiene, der gewaehlte hebt sich als weisses
// Plaettchen heraus. Ein eingelassener Knopf (Schatten nach
// innen) war der erste Versuch und sah schmutzig aus.
//
// ALLE BEDIENELEMENTE EINER REIHE SIND GLEICH HOCH. Die Schiene
// misst 36, die Plaettchen darin 32. Wer hier etwas ergaenzt,
// nimmt `PLAETTCHEN` — und laesst `npm run design:check` laufen.
// ============================================================

"use client";

import {
  Children,
  createContext,
  Fragment,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  Check,
  ChevronDown,
  Search,
  SlidersHorizontal,
  Sparkles,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { buttonVariants } from "@/components/ui/button";
import { PanelTrenner, PanelZeile, Steuerpanel } from "@/components/ui/panel";
import { VIEW_PARAM } from "@/lib/saved-views";
import { cn } from "@/lib/utils";
import { noAutocorrect } from "@/components/ui/no-autocorrect";
import { SavedViews } from "./saved-views";

export type Person = {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  /** Die gewaehlte Farbe des Kreises (`User.color`). */
  color?: string | null;
  anzahl: number;
};

/**
 * Schreiben in die Adresse.
 *
 * Alles Steuernde einer Liste steht in der ADRESSE und nicht in
 * einem Zustand im Browser: eine Ansicht wie „offene Rechnungen
 * von Weber im dritten Quartal" laesst sich so verschicken, als
 * Lesezeichen ablegen und neu laden — und die Seite bleibt eine
 * Server-Komponente.
 *
 * `optimistic` laeuft im selben Uebergang wie der Seitenwechsel —
 * die Stelle fuer ein `useOptimistic`, das den Knopf sofort
 * umschaltet und erst mit der neuen Seite wieder loslaesst
 * (siehe `sortable-head.tsx`).
 */
export function useAnsicht() {
  const router = useRouter();
  const pfad = usePathname();
  const params = useSearchParams();
  const [isPending, startTransition] = useTransition();

  function setzen(aenderungen: Record<string, string | null>, optimistic?: () => void) {
    const naechste = new URLSearchParams(params.toString());
    // Ein neuer Filter beginnt mit einem neuen Sichtfenster.
    // Wer von „alle" auf „offen" umstellt, will nicht die 1.400
    // Zeilen der vorigen Auswahl weitergeschleppt bekommen —
    // und wer sucht, schon gar nicht. Siehe `lib/sichtfenster.ts`.
    naechste.delete("umfang");
    for (const [name, wert] of Object.entries(aenderungen)) {
      if (wert === null || wert === "") naechste.delete(name);
      else naechste.set(name, wert);
    }
    const frage = naechste.toString();
    startTransition(() => {
      optimistic?.();
      router.replace(frage ? `${pfad}?${frage}` : pfad, { scroll: false });
    });
  }

  return { setzen, isPending };
}

// ── Der Kopf ────────────────────────────────────────────────

/**
 * Wie viele Filter gesetzt sind — `Filterzurueck` meldet es nach
 * oben, der Schalter des Telefons zeigt es an (siehe `Filterkopf`).
 */
const GesetzteFilter = createContext<((anzahl: number) => void) | null>(null);

/**
 * Die zwei Zeilen ueber der Liste.
 *
 * `oben` sind die Sachfilter, `unten` die Personen. Beide Zeilen
 * teilen sich dieselbe Textkante wie die Tabelle darunter.
 *
 * AUF DEM TELEFON (unter `md`) STEHT NUR DIE SUCHE. Umgebrochen
 * waren beide Zeilen auf dem iPhone fuenf, sechs Reihen hoch —
 * rund 300 Pixel, und die erste Zeile der Liste begann erst im
 * unteren Drittel des Bildes. Dort steht deshalb die Suche ueber
 * die volle Breite und daneben „Filter“ mit der Zahl der gesetzten
 * Filter; ein Tipp klappt den ganzen Kopf auf, wie er ist.
 * Gezaehlt wird, was `Filterzurueck` zaehlt — ohne den Knopf
 * steht keine Zahl da.
 *
 * Ausgeblendet wird per CSS jedes Kind der oberen Zeile, das keine
 * Suche enthaelt: die Listen reichen ihre Filter als EIN Bündel
 * herein, und welches Kind die Suche ist, weiss nur der Browser.
 * Ein Platzhalter, der fuer die Suche steht (das Ladegeruest),
 * traegt `data-filtersuche`.
 *
 * `zuklappbar={false}` fuer Koepfe, die keine Filter tragen — die
 * Tafel von Dashboard und Berichten ordnet Kacheln an; „Filter“
 * stuende dort ueber etwas anderem.
 */
export function Filterkopf({
  oben,
  unten,
  isPending,
  zuklappbar = true,
}: {
  oben: React.ReactNode;
  unten?: React.ReactNode;
  isPending?: boolean;
  zuklappbar?: boolean;
}) {
  const [aufgeklappt, setOffen] = useState(false);
  const [gesetzt, setGesetzt] = useState(0);
  const offen = aufgeklappt || !zuklappbar;

  return (
    <GesetzteFilter.Provider value={setGesetzt}>
      <div className={cn("border-border border-b", isPending && "opacity-60")}>
        <div
          className={cn(
            "flex flex-wrap items-center gap-x-2 gap-y-2 px-4 py-2.5",
            !offen &&
              "max-md:[&>*:not(:has(input[type=search])):not([data-filtersuche]):not([data-filterschalter])]:hidden",
          )}
        >
          {oben}
          {zuklappbar && (
            <div data-filterschalter="" className="order-last md:hidden">
              <Schiene>
                <Waehler
                  icon={SlidersHorizontal}
                  aktiv={offen}
                  aria-expanded={offen}
                  aria-label={gesetzt > 0 ? `Filter, ${gesetzt} gesetzt` : "Filter"}
                  onClick={() => setOffen((o) => !o)}
                >
                  Filter
                  {gesetzt > 0 && (
                    <span className="numeric text-body-sm text-accent font-medium">{gesetzt}</span>
                  )}
                </Waehler>
              </Schiene>
            </div>
          )}
        </div>
        {unten && (
          <div
            className={cn(
              "border-border-subtle flex flex-wrap items-center gap-x-2 gap-y-2 border-t px-4 py-2",
              !offen && "max-md:hidden",
            )}
          >
            {unten}
          </div>
        )}
      </div>
    </GesetzteFilter.Provider>
  );
}

// ── Schiene und Plaettchen ──────────────────────────────────

/**
 * Die graue Schiene, auf der eine Gruppe von Knoepfen liegt.
 *
 * Sie ist der Grund, warum der gewaehlte Knopf sauber aussieht:
 * das weisse Plaettchen braucht einen dunkleren Grund, um sich
 * abzuheben.
 *
 * Auf dem Telefon wird sie nie breiter als ihr Platz und rollt
 * seitlich: die Darstellungswahl eines Projekts (Board, Liste,
 * Kalender, Zeitplan) war breiter als das iPhone und schob die
 * ganze Seite um 20 Pixel aus dem Bild.
 */
export function Schiene({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "bg-bg-secondary ring-border-subtle flex h-9 items-center gap-0.5 rounded-[10px] p-0.5 ring-1 ring-inset",
        "max-md:max-w-full max-md:[scrollbar-width:none] max-md:overflow-x-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Die Fuge zwischen zwei Gruppen auf derselben Schiene. */
export function Fuge() {
  // In der aufgeklappten Zaehlschiene liegt die Gruppe untereinander —
  // dort wird aus dem senkrechten Strich ein waagerechter.
  if (useContext(ImPanel)) return <PanelTrenner />;
  return <span className="bg-border/70 mx-0.5 h-4 w-px shrink-0" aria-hidden />;
}

/**
 * Der mitwachsende Zwischenraum, der alles Folgende nach rechts
 * schiebt — solange die Zeile reicht.
 *
 * Nicht `ml-auto`: eine automatische Marge wirkt INNERHALB der
 * Zeile, in der ihr Element gerade liegt. Bricht der Filterkopf
 * um, steht die Zaehlerschiene allein in Zeile zwei — und die
 * Marge drueckt sie dort an den rechten Rand, weit weg von der
 * Textkante, an der Suche und Tabelle beginnen. Der Zwischenraum
 * bleibt beim Umbruch in Zeile eins zurueck und schrumpft dort
 * auf null; was umbricht, faengt links an.
 */
export function Luecke() {
  return <span className="flex-1" aria-hidden />;
}

export const PLAETTCHEN = cn(
  "duration-fast ease-standard flex h-8 shrink-0 items-center rounded-lg transition-all",
  "focus-visible:ring-accent/40 focus-visible:ring-2 focus-visible:outline-none",
);

export const PLAETTCHEN_AN = "bg-bg-primary text-text-primary shadow-e1";
export const PLAETTCHEN_AUS = "text-text-secondary hover:text-text-primary hover:bg-bg-primary/60";

/**
 * Ein Filter mit seiner Zahl — „Überfällig 8", „Aktiv 68".
 *
 * Die Zahl IST der Filter. Vorher standen solche Zahlen als
 * grosse Kacheln ueber der Liste: sie sagten „acht überfällig"
 * und liessen einen damit allein.
 */
export function Zaehlknopf({
  label,
  anzahl,
  aktiv,
  ton = "normal",
  titel,
  onClick,
}: {
  label: string;
  anzahl: number;
  aktiv: boolean;
  ton?: "normal" | "gefahr";
  titel?: string;
  onClick: (ergaenzend: boolean) => void;
  /**
   * Eine Abkuerzung ueber mehrere Zustaende — „Offen", „Gestellt",
   * „Alle". Nur fuer die Zaehlschiene: steht sie gedrueckt, nennt
   * der Knopf der Schiene SIE und nicht die einzelnen Zustaende,
   * die mit ihr gedrueckt sind.
   */
  sammel?: boolean;
}) {
  const imPanel = useContext(ImPanel);

  if (imPanel) {
    return (
      <PanelZeile
        aria-pressed={aktiv}
        title={titel}
        aktiv={aktiv}
        onClick={(e) => onClick(e.metaKey || e.ctrlKey || e.shiftKey)}
        className={cn(
          "py-1.5",
          // Heller als die uebliche Panelwahl: hier stehen oft vier,
          // fuenf gewaehlte Zeilen untereinander, und in Tertiaer
          // wurde daraus eine dunkle Saeule. Der Haken traegt die
          // Wahl, die Flaeche begleitet sie nur.
          aktiv ? "bg-bg-secondary" : "hover:bg-bg-secondary/60",
          anzahl === 0 && !aktiv && "opacity-45",
        )}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <span
          className={cn(
            "numeric text-body-sm font-medium",
            ton === "gefahr" && anzahl > 0 ? "text-danger" : "text-text-primary",
          )}
        >
          {anzahl}
        </span>
        <span className="flex w-3.5 shrink-0 justify-center" aria-hidden>
          {aktiv && <Check className="text-accent h-3.5 w-3.5" />}
        </span>
      </PanelZeile>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={aktiv}
      title={titel}
      onClick={(e) => onClick(e.metaKey || e.ctrlKey || e.shiftKey)}
      className={cn(
        PLAETTCHEN,
        "gap-1.5 px-2",
        aktiv ? PLAETTCHEN_AN : PLAETTCHEN_AUS,
        anzahl === 0 && !aktiv && "opacity-45",
      )}
    >
      <span className={cn("text-caption", aktiv && "font-medium")}>{label}</span>
      <span
        className={cn(
          "numeric text-body-sm font-medium",
          ton === "gefahr" && anzahl > 0 ? "text-danger" : "text-text-primary",
        )}
      >
        {anzahl}
      </span>
    </button>
  );
}

// ── Die Zaehlschiene ────────────────────────────────────────

/** Steht ein Zaehlknopf (oder eine Fuge) in der aufgeklappten Liste? */
const ImPanel = createContext(false);

type Zaehler = {
  label: string;
  anzahl: number;
  aktiv: boolean;
  ton?: "normal" | "gefahr";
  sammel?: boolean;
};

/**
 * Die Zaehlknoepfe unter den Kindern, wie sie auf der Schiene
 * stuenden — auch aus Listen (`.map`), Fragmenten und schlichten
 * Huellen (`<span className="contents">`) heraus. Ein Zaehlknopf,
 * der in einer anderen KOMPONENTE steckt, wird hier nicht gesehen;
 * er waere dann im Panel, fehlte aber im Knopf.
 */
function zaehlerIn(kinder: React.ReactNode): Zaehler[] {
  const gefunden: Zaehler[] = [];
  Children.forEach(kinder, (kind) => {
    if (!isValidElement(kind)) return;
    if (kind.type === Zaehlknopf) gefunden.push(kind.props as Zaehler);
    else if (kind.type === Fragment || typeof kind.type === "string")
      gefunden.push(...zaehlerIn((kind.props as { children?: React.ReactNode }).children));
  });
  return gefunden;
}

/**
 * Was der Knopf der Zaehlschiene sagt: die gedrueckte Abkuerzung
 * („Offen 8"), sonst die gedrueckten Zustaende mit ihrer Summe
 * („Versendet + Bezahlt 158", „Versendet +2 164").
 */
function zusammenfassung(zaehler: Zaehler[]): { text: string; anzahl: number } | null {
  const an = zaehler.filter((z) => z.aktiv);
  const sammel = an.find((z) => z.sammel);
  if (sammel) return { text: sammel.label, anzahl: sammel.anzahl };
  if (an.length === 0) return null;
  const anzahl = an.reduce((summe, z) => summe + z.anzahl, 0);
  if (an.length === 1) return { text: an[0].label, anzahl };
  if (an.length === 2) return { text: `${an[0].label} + ${an[1].label}`, anzahl };
  return { text: `${an[0].label} +${an.length - 1}`, anzahl };
}

/**
 * Eine Schiene voller Zaehlknoepfe, zusammengeklappt auf EINEN
 * Knopf — „Status  Offen 8".
 *
 * Die Rechnungsliste trug neun Zaehler nebeneinander, gut 900 px,
 * und jede andere Liste ihre eigene lange Reihe. Gebraucht wird
 * davon fast immer nur der eine, der gerade gilt. Der steht jetzt
 * im Knopf; die uebrigen klappen beim Darueberfahren als Liste
 * auf, mit Zahl und Haken, und ein Klick schaltet wie bisher —
 * die Liste bleibt dabei offen, man kann mehrere nacheinander
 * waehlen. Ein Klick auf den Knopf haelt sie fest.
 *
 * Die Kinder sind DIESELBEN wie auf der `Schiene`: `Zaehlknopf`
 * und `Fuge` merken am Kontext, dass sie im Panel liegen, und
 * zeichnen sich als Zeile bzw. Trennlinie. Abkuerzungen tragen
 * `sammel`, damit der Knopf sie nennt.
 *
 * WAS ROT IST, BLEIBT SICHTBAR. Hat ein Zaehler mit `ton="gefahr"`
 * etwas zu melden („Überfällig 6"), traegt der Knopf einen roten
 * Punkt — eine Schieflage darf nicht hinter einem Darueberfahren
 * verschwinden.
 *
 * Nur fuer Schienen ab drei Zaehlern. Ein oder zwei Schalter
 * („Ungelesen", „USt-IdNr. prüfen") bleiben auf der `Schiene`:
 * zusammengeklappt waeren sie nicht kuerzer, nur einen Griff
 * weiter weg.
 */
export function Zaehlschiene({
  titel,
  children,
}: {
  /** Wonach die Schiene fragt — „Status", „Zustand". */
  titel: string;
  children: React.ReactNode;
}) {
  const zaehler = zaehlerIn(children);
  const stand = zusammenfassung(zaehler);
  const rot = zaehler.find((z) => z.ton === "gefahr" && z.anzahl > 0);

  return (
    <Schiene>
      <Steuerpanel
        beimUeberfahren
        breite="schmal"
        ausrichtung="start"
        abstand={6}
        ausloeser={
          <Waehler
            aktiv={Boolean(stand)}
            aria-label={stand ? `${titel}: ${stand.text}, ${stand.anzahl}` : titel}
          >
            <span
              className={cn(
                "text-caption font-normal",
                stand ? "text-text-tertiary" : "text-text-secondary",
              )}
            >
              {titel}
            </span>
            {stand && (
              <>
                <span className="max-w-[12rem] truncate">{stand.text}</span>
                <span className="numeric text-body-sm text-text-primary font-medium">
                  {stand.anzahl}
                </span>
              </>
            )}
            {rot && (
              <span
                className="bg-danger h-1.5 w-1.5 shrink-0 rounded-full"
                title={`${rot.label}: ${rot.anzahl}`}
                aria-hidden
              />
            )}
          </Waehler>
        }
      >
        {/* Luft zwischen den Zeilen: gewaehlte Zeilen tragen eine
            Flaeche, und zwei davon untereinander verschmolzen ohne
            Abstand zu einem Block. Die `contents`-Huellen mancher
            Listen stoeren das nicht — ihre Kinder sind die Glieder. */}
        <div className="flex flex-col gap-1.5">
          <ImPanel.Provider value>{children}</ImPanel.Provider>
        </div>
      </Steuerpanel>
    </Schiene>
  );
}

/**
 * Ein Knopf, der eine Flaeche aufklappt — Kunde, Zeitachse,
 * Gruppierung.
 *
 * DIE BESCHRIFTUNG SAGT, WAS ER TUT, und nicht nur, worauf er
 * gerade steht: „Gruppe: Kunde" statt „Kunde". Sonst steht in
 * einer Liste ein Knopf „Kunde", der filtert, und in der
 * naechsten einer, der gruppiert — und niemand weiss, welcher
 * gerade gemeint ist.
 *
 * `...rest` MUSS durchgereicht werden: Radix haengt den Oeffner
 * (onClick, aria-expanded, ref) per `asChild` an dieses Element.
 */
export function Waehler({
  icon: Icon,
  aktiv,
  mitWinkel = true,
  children,
  className,
  ...rest
}: {
  icon?: LucideIcon;
  aktiv?: boolean;
  /** Der Winkel sagt „hier klappt etwas auf". Kein Panel, kein Winkel. */
  mitWinkel?: boolean;
  children: React.ReactNode;
} & React.ComponentPropsWithRef<"button">) {
  return (
    <button
      type="button"
      aria-pressed={aktiv}
      {...rest}
      className={cn(
        PLAETTCHEN,
        "text-body-sm gap-1.5 px-2",
        aktiv ? cn(PLAETTCHEN_AN, "font-medium") : PLAETTCHEN_AUS,
        className,
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      {children}
      {mitWinkel && <ChevronDown className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />}
    </button>
  );
}

/**
 * Das Kreuz, das eine Auswahl wieder loest.
 *
 * Es sitzt NEBEN dem Waehler auf derselben Schiene und nicht in
 * ihm: ein Knopf im Knopf ist kein gueltiges Markup und faengt
 * den Klick am falschen Ende.
 */
export function Loeschkreuz({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(PLAETTCHEN, "text-text-tertiary hover:text-text-primary w-7 justify-center")}
    >
      <X className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

// ── Das Auswahlfeld ─────────────────────────────────────────

export type Auswahl = {
  id: string;
  name: string;
  /**
   * Wie viele Zeilen darauf entfallen — beim FILTER die halbe
   * Auskunft („Weber 12").
   *
   * Offen, weil dieselbe Liste auch WAEHLT statt zu filtern:
   * beim Zuweisen einer Ausgabe an ein Projekt gaebe es nichts
   * zu zaehlen, und eine Spalte aus Nullen waere schlimmer als
   * keine.
   */
  anzahl?: number;
  /** Zweite Zeile im Panel — Nummer, Kunde, was unterscheidet. */
  hinweis?: string;
};

/**
 * Ein Waehler mit durchsuchbarer Liste — Kunde, Projekt,
 * Leistung.
 *
 * Es gab davon drei Abschriften: eine in der Rechnungsliste,
 * eine in der Angebotsliste, eine im Stundenauszug. Alle drei
 * hiessen `CompanyList` oder `Kundenliste`, alle drei konnten
 * dasselbe, und alle drei waren an einer anderen Stelle schon
 * leicht verschieden — mal stand die Zahl rechts, mal gar nicht,
 * mal schloss das Panel nach der Wahl, mal nicht.
 *
 * Deshalb steht es hier: EIN Feld, das alles mitbringt, was zu
 * einer Auswahl gehoert — Suche, „Alle …", die Zahl je Eintrag,
 * der Haken beim Gewaehlten und das Kreuz zum Aufheben. Das
 * Kreuz ist ein Geschwister und kein Kind: ein Knopf im Knopf
 * ist kein gueltiges Markup (siehe `Loeschkreuz`).
 */
export function Auswahlfeld({
  icon,
  titel,
  label,
  eintraege,
  gewaehlt,
  onWaehlen,
  suchtext,
  alleLabel,
  leerText,
  breite = "mittel",
}: {
  icon?: LucideIcon;
  /** Ueber dem Panel — „Kunde". */
  titel: string;
  /** Am Knopf, solange nichts gewaehlt ist — „Kunde". */
  label: string;
  eintraege: Auswahl[];
  gewaehlt: string;
  onWaehlen: (id: string | null) => void;
  suchtext: string;
  alleLabel: string;
  leerText: string;
  breite?: "schmal" | "mittel" | "breit";
}) {
  const aktiv = eintraege.find((e) => e.id === gewaehlt);

  return (
    <>
      <Steuerpanel
        breite={breite}
        ausrichtung="start"
        titel={titel}
        ausloeser={
          <Waehler icon={icon} aktiv={Boolean(gewaehlt)}>
            <span className="max-w-[9rem] truncate">{aktiv ? aktiv.name : label}</span>
          </Waehler>
        }
      >
        <Auswahlliste
          eintraege={eintraege}
          gewaehlt={gewaehlt}
          onWaehlen={onWaehlen}
          suchtext={suchtext}
          alleLabel={alleLabel}
          leerText={leerText}
        />
      </Steuerpanel>
      {gewaehlt && (
        <Loeschkreuz label={`${titel}filter aufheben`} onClick={() => onWaehlen(null)} />
      )}
    </>
  );
}

/** Der Inhalt des Panels — getrennt, weil ihn auch andere Flaechen brauchen. */
export function Auswahlliste({
  eintraege,
  gewaehlt,
  onWaehlen,
  suchtext,
  alleLabel,
  aufhebenLabel = "Auswahl aufheben",
  leerText,
}: {
  eintraege: Auswahl[];
  gewaehlt: string;
  onWaehlen: (id: string | null) => void;
  suchtext: string;
  alleLabel: string;
  /**
   * Die oberste Zeile, SOLANGE etwas gewaehlt ist. „Auswahl
   * aufheben" stimmt fuer einen Filter; wo die Liste zuweist,
   * heisst dasselbe „Zuweisung aufheben".
   */
  aufhebenLabel?: string;
  leerText: string;
}) {
  const [suche, setSuche] = useState("");

  const gezeigt = useMemo(() => {
    const n = suche.trim().toLowerCase();
    if (!n) return eintraege;
    return eintraege.filter(
      (e) => e.name.toLowerCase().includes(n) || (e.hinweis ?? "").toLowerCase().includes(n),
    );
  }, [eintraege, suche]);

  return (
    <>
      <input
        autoFocus
        type="search"
        {...noAutocorrect}
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        placeholder={suchtext}
        aria-label={suchtext}
        className="border-border bg-bg-primary text-body-sm text-text-primary placeholder:text-text-tertiary duration-fast hover:border-border-strong focus:border-accent/40 focus:ring-accent/30 mx-1 mb-1 block h-9 w-[calc(100%-8px)] rounded-lg border px-2.5 transition-colors focus:ring-2 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
      />

      <PanelZeile aktiv={!gewaehlt} onClick={() => onWaehlen(null)}>
        <span className="min-w-0 flex-1">{gewaehlt ? aufhebenLabel : alleLabel}</span>
        {!gewaehlt && <Check className="text-accent h-3.5 w-3.5 shrink-0" aria-hidden />}
      </PanelZeile>

      {gezeigt.map((e) => (
        <PanelZeile
          key={e.id}
          aktiv={gewaehlt === e.id}
          onClick={() => onWaehlen(gewaehlt === e.id ? null : e.id)}
        >
          <span className="min-w-0 flex-1 truncate">
            {e.name}
            {e.hinweis && (
              <span className="text-caption text-text-tertiary block truncate">{e.hinweis}</span>
            )}
          </span>
          {e.anzahl !== undefined && (
            <span className="numeric text-caption text-text-tertiary shrink-0">{e.anzahl}</span>
          )}
          {gewaehlt === e.id && <Check className="text-accent h-3.5 w-3.5 shrink-0" aria-hidden />}
        </PanelZeile>
      ))}

      {gezeigt.length === 0 && (
        <p className="text-caption text-text-tertiary px-2 py-3">{leerText}</p>
      )}
    </>
  );
}

// ── Personen ────────────────────────────────────────────────

/**
 * Eine Rolle mit ihren Gesichtern.
 *
 * „Welche davon gehoeren mir?" ist die zweite Frage an jede
 * Liste. Namen in einem Auswahlfeld waeren dafuer drei Klicks;
 * Gesichter nebeneinander sind einer. Gezeigt wird nur, wer in
 * dieser Rolle auch wirklich vorkommt.
 *
 * DIE GESICHTER STEHEN IN EINER LEISTE UNTER DEM KNOPF, nicht
 * mehr offen in der Zeile. Drei Rollen mit je vier Kreisen (die
 * Rechnungsliste) waren eine halbe Bildschirmbreite, die ueber
 * jeder Liste stand, auch wenn niemand nach Personen fragte. Der
 * Knopf oeffnet beim Darueberfahren (`beimUeberfahren`), ein
 * Klick haelt die Leiste fest; gewaehlt wird weiter mit einem
 * Klick aufs Gesicht, auch mehrere nacheinander. Wer gewaehlt
 * ist, steht als kleiner Kreis IM Knopf — die Auswahl bleibt
 * sichtbar, ohne dass die Leiste offen ist.
 *
 * Der Name zum Gesicht steht in der Zeile unter der Leiste und
 * nicht als Tooltip: der haengt am Kreis, und das Panel schneidet
 * ihn ab.
 */
export function Personenreihe({
  label,
  hinweis,
  personen,
  gewaehlt,
  onWaehlen,
  einheit = ["Eintrag", "Einträge"],
}: {
  label: string;
  hinweis?: string;
  personen: Person[];
  gewaehlt: string[];
  onWaehlen: (id: string) => void;
  /** Einzahl und Mehrzahl fuer den Zeigetext. */
  einheit?: [string, string];
}) {
  const [unterZeiger, setUnterZeiger] = useState<Person | null>(null);

  if (personen.length === 0) return null;

  const auswahl = personen.filter((p) => gewaehlt.includes(p.id));
  const zeile = unterZeiger
    ? `${unterZeiger.firstName} ${unterZeiger.lastName} · ${unterZeiger.anzahl} ${unterZeiger.anzahl === 1 ? einheit[0] : einheit[1]}`
    : (hinweis ?? label);

  return (
    <Schiene>
      <Steuerpanel
        beimUeberfahren
        breite="passend"
        ausrichtung="start"
        abstand={6}
        ausloeser={
          <Waehler
            aktiv={auswahl.length > 0}
            aria-label={
              auswahl.length > 0
                ? `${label}: ${auswahl.map((p) => `${p.firstName} ${p.lastName}`).join(", ")}`
                : label
            }
          >
            {label}
            {auswahl.length > 0 && (
              <span className="flex items-center -space-x-1.5" aria-hidden>
                {auswahl.slice(0, 3).map((p) => (
                  <Avatar
                    key={p.id}
                    firstName={p.firstName}
                    lastName={p.lastName}
                    imageUrl={p.avatarUrl ?? null}
                    color={p.color}
                    size="sm"
                    className="ring-bg-primary h-5 w-5 text-[9px] ring-2"
                  />
                ))}
                {auswahl.length > 3 && (
                  <span className="numeric text-caption text-text-secondary pl-2.5">
                    +{auswahl.length - 3}
                  </span>
                )}
              </span>
            )}
          </Waehler>
        }
      >
        {/* Mindestbreite, damit der Text darunter nicht bei jedem
            Gesicht die Breite wechselt. `contain: inline-size` laesst
            ihn nicht mitreden, wie breit das Panel wird — er fuellt
            nur, was die Gesichter vorgeben. `w-0 min-w-full` war der
            erste Versuch: die Prozentangabe laeuft in einem Panel,
            das so breit ist wie sein Inhalt, ins Leere, und Safari
            brach den Namen nach jedem Wort um. */}
        <div className="min-w-60">
          <div className="flex flex-wrap items-center gap-1">
            {personen.map((p) => {
              const an = gewaehlt.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={an}
                  aria-label={`${label}: ${p.firstName} ${p.lastName}`}
                  onClick={() => onWaehlen(p.id)}
                  onPointerEnter={() => setUnterZeiger(p)}
                  onPointerLeave={() => setUnterZeiger(null)}
                  onFocus={() => setUnterZeiger(p)}
                  onBlur={() => setUnterZeiger(null)}
                  className={cn(
                    "duration-fast ease-standard block rounded-full transition-all",
                    "focus-visible:ring-accent/40 focus-visible:ring-2 focus-visible:outline-none",
                    // Der Ring liegt INNEN: aussen liegend waere der
                    // Kreis im gewaehlten Zustand groesser als seine
                    // Nachbarn und die Reihe wuerde springen.
                    an
                      ? "ring-accent-2 ring-2 ring-inset"
                      : gewaehlt.length > 0
                        ? "opacity-40 hover:opacity-100"
                        : "hover:ring-border-strong hover:ring-2 hover:ring-inset",
                  )}
                >
                  <Avatar
                    firstName={p.firstName}
                    lastName={p.lastName}
                    imageUrl={p.avatarUrl ?? null}
                    color={p.color}
                    size="md"
                  />
                </button>
              );
            })}
          </div>
          <p
            className={cn(
              "border-border-subtle text-caption mt-1.5 border-t px-1 pt-1.5 [contain:inline-size]",
              unterZeiger ? "text-text-primary" : "text-text-tertiary",
            )}
          >
            {zeile}
          </p>
        </div>
      </Steuerpanel>
    </Schiene>
  );
}

/**
 * „Meine" — der eine Filter, den man taeglich greift. Er gehoert
 * in die Zeile und nicht unter einen Knopf, und er steht vor den
 * Gesichtern: Projekte (Verantwortung), Aufgaben (Beteiligung).
 *
 * `h-9` statt der 32 aus PLAETTCHEN: das Plaettchen ist auf die
 * Schiene gerechnet — 32 innen, die Schiene aussen 36. Hier steht
 * keine Schiene darum; in der Zeile daneben sind die Gesichter und
 * das Zuruecksetzen 36 hoch. `npm run design:check` hat es als
 * „Meine: 32px neben FS: 36px" gemeldet.
 *
 * Ein Schalter und keine Kennung in der Adresse (`meine=1`): eine
 * geteilte Ansicht mit „Meine" zeigt jedem SEINE Eintraege.
 */
export function MineToggle({
  active,
  onToggle,
  label = "Meine",
  icon: Icon = UserRound,
  fillWhenActive = false,
}: {
  active: boolean;
  onToggle: () => void;
  label?: string;
  /** Dieselbe Gestalt fuer einen zweiten Schalter dieser Art („Favoriten"). */
  icon?: LucideIcon;
  /** Das Zeichen gefuellt, solange der Schalter an ist — der Stern. */
  fillWhenActive?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onToggle}
      className={cn(
        PLAETTCHEN,
        "text-body-sm h-9 gap-1.5 px-2",
        active ? cn(PLAETTCHEN_AN, "font-medium") : PLAETTCHEN_AUS,
      )}
    >
      <Icon
        className={cn("h-3.5 w-3.5 shrink-0", active && fillWhenActive && "fill-current")}
        aria-hidden
      />
      {label}
    </button>
  );
}

// ── Suche und Zuruecksetzen ─────────────────────────────────

/**
 * Suchfeld mit Verzoegerung.
 *
 * Der Text steht im Zustand, damit das Tippen fluessig bleibt;
 * die Adresse zieht 300 ms spaeter nach. Ohne das laeuft bei
 * „Relaunch" acht Mal eine Abfrage, und die Ergebnisse treffen
 * in beliebiger Reihenfolge ein. Bei einer Aenderung von aussen
 * (Zuruecksetzen, Zurueck-Taste) uebernimmt das Feld den neuen
 * Wert — geprueft waehrend des Renderns, nicht in einem Effekt,
 * sonst steht einen Durchgang lang der alte Text da.
 */
export function Suchfeld({
  wert,
  platzhalter,
  beschriftung,
  param = "q",
  frage,
  fullWidth = false,
}: {
  wert: string;
  platzhalter: string;
  beschriftung: string;
  param?: string;
  /**
   * Fuellt die Breite, statt beim Hineinklicken zu wachsen — fuer
   * eine schmale Spalte, in der das Feld schon alles ist, was die
   * Kopfzeile traegt (Notizen im Projekt).
   */
  fullWidth?: boolean;
  /**
   * Macht aus dem Suchfeld zusaetzlich eine Frage-Zeile.
   *
   * Eine Liste hat zwei Arten von Eingabe, die sich gleich
   * anfuehlen: „Weber“ sucht, „was muss ich bei Weber
   * abrechnen“ fragt. Getrennte Felder dafuer waeren zwei
   * Felder nebeneinander, in die dasselbe getippt wird.
   *
   * Deshalb dasselbe Feld: ab vier Woertern — oder einem
   * Fragezeichen — erscheint der Hinweis, dass die Eingabe(ta)
   * als Frage gestellt werden kann. Die Suche laeuft daneben
   * weiter; erst die Eingabetaste fragt.
   *
   * Ohne diese Angabe bleibt das Feld, was es ueberall sonst
   * ist: ein Filter.
   */
  frage?: { ausloesen: (text: string) => void; laeuft?: boolean };
}) {
  const { setzen } = useAnsicht();
  const [text, setText] = useState(wert);
  const [letzter, setLetzter] = useState(wert);
  const uhr = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Was nach einer Frage aussieht: ein Fragezeichen oder ein
  // halber Satz. Zwei Woerter sind ein Projektname, vier sind
  // eine Frage.
  const klingtNachFrage =
    Boolean(frage) && (text.trim().endsWith("?") || text.trim().split(/\s+/).length >= 4);

  if (letzter !== wert) {
    setLetzter(wert);
    setText(wert);
  }

  useEffect(
    () => () => {
      if (uhr.current) clearTimeout(uhr.current);
    },
    [],
  );

  function tippen(neu: string) {
    setText(neu);
    if (uhr.current) clearTimeout(uhr.current);
    // Ein halber Satz ist kein Suchbegriff: „was muss ich bei
    // Weber abrechnen“ als Filter gelesen leert die Liste und
    // zeigt einen leeren Zustand, waehrend der Mensch noch
    // tippt. Er wandert deshalb NICHT in die Adresse — gefiltert
    // wird erst wieder, wenn daraus ein Begriff wird.
    const alsFrage =
      Boolean(frage) && (neu.trim().endsWith("?") || neu.trim().split(/\s+/).length >= 4);
    uhr.current = setTimeout(() => setzen({ [param]: alsFrage ? null : neu.trim() || null }), 300);
  }

  return (
    // Auf dem Telefon so breit wie moeglich: zugeklappt steht die
    // Suche allein mit dem Filter-Schalter in der Zeile. Die Basis
    // von 12rem haelt sie aufgeklappt davon ab, sich neben den
    // Waehlern auf Daumenbreite stauchen zu lassen — was nicht
    // daneben passt, bricht um.
    <div
      className={cn(
        "relative max-md:min-w-0 max-md:shrink max-md:grow max-md:basis-48",
        fullWidth && "min-w-0 flex-1",
      )}
    >
      <Search
        className="text-text-tertiary pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2"
        aria-hidden
      />
      <input
        type="search"
        {...noAutocorrect}
        value={text}
        onChange={(e) => tippen(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            tippen("");
          }
          if (e.key === "Enter" && klingtNachFrage && !frage?.laeuft) {
            e.preventDefault();
            frage?.ausloesen(text.trim());
          }
        }}
        placeholder={platzhalter}
        aria-label={beschriftung}
        className={cn(
          "border-border bg-bg-primary text-body-sm text-text-primary placeholder:text-text-tertiary duration-normal ease-standard hover:border-border-strong focus:border-accent/40 focus:ring-accent/30 h-9 rounded-lg border pr-7 pl-8 transition-[width,border-color] focus:ring-2 focus:outline-none [&::-webkit-search-cancel-button]:hidden",
          fullWidth ? "w-full" : "w-48 focus:w-64 max-md:w-full max-md:focus:w-full",
        )}
      />
      {text && (
        <button
          type="button"
          onClick={() => tippen("")}
          aria-label="Suche leeren"
          className="text-text-tertiary duration-fast hover:bg-bg-tertiary hover:text-text-primary absolute top-1/2 right-1.5 grid h-6 w-6 -translate-y-1/2 place-items-center rounded transition-colors"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      )}

      {/* Der Hinweis liegt UEBER dem, was darunter steht
          (absolut), statt die Zeile auseinanderzuschieben: ein
          Filterkopf, der beim Tippen waechst, schiebt die ganze
          Liste nach unten. */}
      {klingtNachFrage && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => frage?.ausloesen(text.trim())}
          disabled={frage?.laeuft}
          className="border-border bg-bg-primary text-caption text-text-secondary hover:border-accent/40 hover:text-text-primary duration-fast absolute top-full left-0 z-20 mt-1 flex items-center gap-1.5 rounded-lg border px-2 py-1.5 whitespace-nowrap shadow-sm transition-colors disabled:opacity-60"
        >
          <Sparkles className="text-accent h-3 w-3 shrink-0" aria-hidden />
          {frage?.laeuft ? "Claude stellt die Liste ein…" : "Als Frage stellen"}
          {!frage?.laeuft && (
            <kbd className="text-text-tertiary border-border-subtle ml-1 rounded border px-1 text-[10px]">
              ⏎
            </kbd>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Derselbe Handgriff als vollwertiger Knopf — fuer den leeren
 * Zustand.
 *
 * Eine Liste, die wegen eines Filters leer ist, ist eine
 * Sackgasse: der Filter steht oben im Kopf, aber der Blick ist
 * in der Mitte der Flaeche. Der Weg heraus gehoert dorthin, wo
 * man gerade hinsieht.
 */
export function Filterloesen({
  params,
  children,
}: {
  params: readonly string[];
  children?: React.ReactNode;
}) {
  const { setzen } = useAnsicht();

  return (
    <button
      type="button"
      onClick={() =>
        setzen({ ...Object.fromEntries(params.map((p) => [p, null])), [VIEW_PARAM]: null })
      }
      className={buttonVariants({ variant: "outline" })}
    >
      <X className="h-4 w-4" aria-hidden />
      {children ?? "Filter zurücksetzen"}
    </button>
  );
}

/**
 * „Filter zurücksetzen (3)" — ganz rechts in der zweiten Zeile,
 * davor die gespeicherten Ansichten (`saved-views.tsx`).
 *
 * Auch ohne gesetzten Filter sichtbar, nur stumm: der Platz
 * gehoert ihm, und eine Zeile, die beim Filtern die Laenge
 * wechselt, springt.
 *
 * Die Ansichten stehen HIER und nicht in jeder Liste einzeln:
 * beide Knoepfe handeln vom ganzen Filter, und `params` — die
 * Schluessel, die das Zuruecksetzen leert — sind genau die, die
 * eine Ansicht speichert. So hat jede Liste mit diesem Knopf
 * auch Ansichten, ohne dass sie etwas dafuer tut.
 *
 * Bringt seine `Luecke` selbst mit — beide sind Kinder der
 * Zeile, darum das Fragment.
 */
export function Filterzurueck({
  anzahl,
  params,
  savedViews = true,
  shapeParams,
  pinnable,
}: {
  anzahl: number;
  params: readonly string[];
  /**
   * Aus nur, wo es keinen Sinn hat: im Kundenbereich (dort gibt
   * es kein „für alle").
   */
  savedViews?: boolean;
  /** Weiter an `SavedViews`: was die Liste formt, ohne zu filtern. */
  shapeParams?: readonly string[];
  /** Weiter an `SavedViews`: Anheften an die Berichte-Uebersicht. */
  pinnable?: boolean;
}) {
  const { setzen } = useAnsicht();

  // Die Zahl geht an den Filter-Schalter des Telefons (`Filterkopf`).
  const melden = useContext(GesetzteFilter);
  useEffect(() => {
    melden?.(anzahl);
  }, [melden, anzahl]);

  return (
    <>
      <Luecke />
      {/* Eine Gruppe: die Zeile setzt 24 px zwischen ihre Kinder,
          die zwei gehoeren aber zusammen. */}
      <div className="flex shrink-0 items-center gap-1">
        {savedViews && <SavedViews params={params} shapeParams={shapeParams} pinnable={pinnable} />}
        <button
          type="button"
          onClick={() =>
            setzen({ ...Object.fromEntries(params.map((p) => [p, null])), [VIEW_PARAM]: null })
          }
          disabled={anzahl === 0}
          className={cn(
            "text-caption duration-fast flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2.5 transition-colors",
            anzahl === 0
              ? "text-text-tertiary/50 cursor-default"
              : "text-text-secondary hover:bg-bg-tertiary hover:text-text-primary",
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          {anzahl === 0 ? "Kein Filter gesetzt" : `Filter zurücksetzen (${anzahl})`}
        </button>
      </div>
    </>
  );
}
